import { useState } from 'react'
import { api } from '../api/client.ts'

/**
 * ЛЕКАРСТВО
 *
 * Расписание задаётся двумя вещами: по каким дням и во сколько.
 * Разделять их важно: «дважды в день» и «по понедельникам и четвергам» —
 * разные вопросы, и мешать их в одно поле значит заставить человека
 * думать за программу.
 *
 * Время приёма — это ещё и время напоминания. Поэтому пустое расписание
 * допустимо: лекарство «по необходимости» просто не напоминает о себе.
 */

export type MedicationDraft = {
  id?: string
  name: string
  dosage?: string | null
  form?: string | null
  rrule?: string | null
  times?: string[]
  startsOn?: string | null
  endsOn?: string | null
  note?: string | null
}

const DAYS = [
  { id: 'MO', name: 'пн' },
  { id: 'TU', name: 'вт' },
  { id: 'WE', name: 'ср' },
  { id: 'TH', name: 'чт' },
  { id: 'FR', name: 'пт' },
  { id: 'SA', name: 'сб' },
  { id: 'SU', name: 'вс' },
]

const onlyDate = (value: string | null | undefined): string =>
  value ? new Date(value).toISOString().slice(0, 10) : ''

export function MedicationSheet({
  draft,
  onClose,
  onSaved,
}: {
  draft: MedicationDraft
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)
  const startRule = draft.rrule ?? ''

  const [name, setName] = useState(draft.name)
  const [dosage, setDosage] = useState(draft.dosage ?? '')
  const [mode, setMode] = useState<'none' | 'daily' | 'days'>(
    !startRule ? 'none' : startRule.includes('BYDAY') ? 'days' : 'daily',
  )
  const [days, setDays] = useState<string[]>(() => {
    const match = /BYDAY=([A-Z,]+)/.exec(startRule)
    return match?.[1]?.split(',') ?? []
  })
  const [times, setTimes] = useState<string[]>(draft.times?.length ? draft.times : ['09:00'])
  const [startsOn, setStartsOn] = useState(onlyDate(draft.startsOn))
  const [endsOn, setEndsOn] = useState(onlyDate(draft.endsOn))
  const [note, setNote] = useState(draft.note ?? '')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const rrule =
        mode === 'none'
          ? null
          : mode === 'daily'
            ? 'FREQ=DAILY'
            : days.length
              ? `FREQ=WEEKLY;BYDAY=${days.join(',')}`
              : null

      const body = {
        name: name.trim(),
        dosage: dosage.trim() || null,
        rrule,
        // Без расписания время приёма ни к чему не привязано
        times: rrule ? times.filter(Boolean) : [],
        startsOn: startsOn || null,
        endsOn: endsOn || null,
        note: note.trim() || null,
      }

      if (draft.id) {
        await api(`/api/health/medications/${draft.id}`, { method: 'PATCH', body })
      } else {
        await api('/api/health/medications', { method: 'POST', body })
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!draft.id || !window.confirm('Убрать лекарство? Отметки о приёмах тоже исчезнут.')) return
    setBusy(true)
    try {
      await api(`/api/health/medications/${draft.id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? 'Лекарство' : 'Новое лекарство'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <label className="field">
          <span className="field-label">Название</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Магний, витамин D"
            maxLength={200}
            autoFocus={!editing}
          />
        </label>

        <label className="field">
          <span className="field-label">Сколько за раз</span>
          <input
            className="input"
            value={dosage}
            onChange={(e) => setDosage(e.target.value)}
            placeholder="1 таблетка, 400 мг"
            maxLength={100}
          />
        </label>

        <label className="field">
          <span className="field-label">По каким дням</span>
          <select
            className="input"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'none' | 'daily' | 'days')}
          >
            <option value="none">По необходимости — не напоминать</option>
            <option value="daily">Каждый день</option>
            <option value="days">По дням недели</option>
          </select>
        </label>

        {mode === 'days' && (
          <div className="days-row">
            {DAYS.map((day) => (
              <button
                key={day.id}
                className={'day-chip' + (days.includes(day.id) ? ' is-picked' : '')}
                onClick={() =>
                  setDays((current) =>
                    current.includes(day.id)
                      ? current.filter((d) => d !== day.id)
                      : [...current, day.id],
                  )
                }
              >
                {day.name}
              </button>
            ))}
          </div>
        )}

        {mode !== 'none' && (
          <>
            <h3 className="sheet-sub">Во сколько</h3>
            <div className="times-row">
              {times.map((time, index) => (
                <span key={index} className="time-item">
                  <input
                    className="input"
                    type="time"
                    value={time}
                    onChange={(e) =>
                      setTimes((current) =>
                        current.map((t, i) => (i === index ? e.target.value : t)),
                      )
                    }
                  />
                  {times.length > 1 && (
                    <button
                      className="time-remove"
                      onClick={() => setTimes((current) => current.filter((_, i) => i !== index))}
                      aria-label="Убрать время"
                    >
                      ✕
                    </button>
                  )}
                </span>
              ))}

              {times.length < 8 && (
                <button className="link-btn" onClick={() => setTimes((current) => [...current, '21:00'])}>
                  + ещё приём
                </button>
              )}
            </div>
          </>
        )}

        <div className="amount-row">
          <label className="field">
            <span className="field-label">Начало курса</span>
            <input
              className="input"
              type="date"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Конец курса</span>
            <input
              className="input"
              type="date"
              value={endsOn}
              onChange={(e) => setEndsOn(e.target.value)}
            />
          </label>
        </div>
        <p className="sheet-note">
          Конец курса можно не указывать — тогда лекарство считается постоянным.
        </p>

        <label className="field">
          <span className="field-label">Заметка</span>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="До еды, запивать водой"
            maxLength={1000}
          />
        </label>

        {error && <div className="sheet-error">{error}</div>}

        <div className="sheet-actions">
          <button className="btn-primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {busy ? '…' : 'Сохранить'}
          </button>
          {editing && (
            <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
              Убрать
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
